import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import FileUpload, { AcceptedFileTypes, SelectedDocument } from "./FileUpload";
import { EndpointType } from "@/constants/api";

interface Payload {
  folder_id: number;
  file_name: string;
  document_master_id?: number;
  size: number;
  mark_as_na?: boolean;
  s3_key: string;
  version_id: string;
}

interface MultipartUploadConfig {
  partSize: number;
  maxConcurrentUploads: number;
}

interface UploadPart {
  PartNumber: number;
  ETag: string;
  version_id: string;
}

interface S3FileUploadProps {
  label: string;
  folderId: number;
  documentTypeId?: number;
  accept?: AcceptedFileTypes[];
  maxFileSize?: number;
  id: string;
  isNA?: boolean;
  disableIsNA?: boolean;
  isDriveUpload?: boolean;
  isUpdate?: boolean;
  updateId?: number;
  selectedDocument?: SelectedDocument;
  onNAChange?: (isNA: boolean) => void;
  onUploadComplete?: (fileData: {
    folder_id: number;
    filename: string;
    s3_key: string;
    version_id: string;
  }) => void;
  onUploadError?: (error: string) => void;
  setForceRefresh?: (forceRefresh: boolean) => void;
  onSuccessUploadCallback?: (payload: Payload) => void;
  endpoint?: string;
}

const S3FileUpload: React.FC<S3FileUploadProps> = ({
  label,
  folderId,
  endpoint = "",
  documentTypeId,
  accept,
  maxFileSize,
  id,
  isNA,
  disableIsNA,
  isDriveUpload = false,
  isUpdate = false,
  updateId,
  selectedDocument,
  onNAChange,
  onUploadComplete,
  onUploadError,
  setForceRefresh,
  onSuccessUploadCallback,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>("");
  const [cancelUpload, setCancelUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<XMLHttpRequest | null>(
    null,
  );
  const [uploadParts, setUploadParts] = useState<UploadPart[]>([]);
  const [uploadId, setUploadId] = useState<string>("");
  const [totalParts, setTotalParts] = useState<number>(0);

  const getMultipartConfig = (fileSize: number): MultipartUploadConfig => {
    if (fileSize <= 5 * 1024 * 1024) {
      // 5MB
      return { partSize: fileSize, maxConcurrentUploads: 1 };
    } else if (fileSize <= 100 * 1024 * 1024) {
      // 100MB
      return { partSize: 5 * 1024 * 1024, maxConcurrentUploads: 2 };
    } else {
      return { partSize: 10 * 1024 * 1024, maxConcurrentUploads: 3 };
    }
  };

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const initiateMultipartUpload = async (fileName: string) => {
    const { authenticatedCall } = actionsHandler("filesUpload");
    const response = await dispatch(
      authenticatedCall({
        method: "post",
        payload: {
          folder_id: folderId,
          filename: fileName,
        },
      }),
    );

    if (!response.ok) throw new Error("Failed to initiate multipart upload");
    return response.data;
  };

  const uploadPart = useCallback(
    async (
      partNumber: number,
      chunk: Blob,
      uploadUrl: string,
      totalParts: number,
    ): Promise<UploadPart> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const partProgress = (event.loaded / event.total) * 100;
            const overallProgress = Math.round(
              (partProgress + (partNumber - 1) * 100) / totalParts,
            );
            console.log("overallProgress: ", overallProgress);
            setUploadProgress((prev) =>
              Math.max(prev, Math.min(overallProgress, 99)),
            ); // Cap at 99% until complete
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            const etag = xhr.getResponseHeader("ETag");
            if (!etag) reject(new Error("No ETag received"));
            resolve({
              PartNumber: partNumber,
              ETag: etag?.replace(/"/g, "") || "",
              version_id: xhr.getResponseHeader("x-amz-version-id") || "",
            });
          } else {
            reject(new Error(`Part upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () =>
          reject(new Error("Network error during part upload"));
        xhr.send(chunk);
      });
    },
    [totalParts, setUploadProgress],
  );

  const getPresignedUrlForPart = async (
    partNumber: number,
    fileName: string,
  ) => {
    const { authenticatedCall } = actionsHandler("filesUpload");
    const response = await dispatch(
      authenticatedCall({
        method: "post",
        payload: {
          folder_id: folderId,
          filename: fileName,
          part_number: partNumber,
          upload_id: uploadId,
        },
      }),
    );

    if (!response.ok)
      throw new Error(`Failed to get presigned URL for part ${partNumber}`);
    return response.data.url;
  };

  const handleFileChange = useCallback(async (newFile: File | null) => {
    setError("");
    setFile(newFile);
    setCancelUpload(false);
  }, []);

  const handleUploadingFile = useCallback(async () => {
    if (!file) return;

    try {
      setIsUploading(true);
      const { partSize, maxConcurrentUploads } = getMultipartConfig(file.size);

      // Initiate multipart upload
      const { uploadId, s3_key } = await initiateMultipartUpload(file.name);
      setUploadId(uploadId);

      // Split file into chunks
      const chunks: Blob[] = [];
      let offset = 0;
      while (offset < file.size) {
        chunks.push(file.slice(offset, offset + partSize));
        offset += partSize;
      }

      setTotalParts(chunks.length);
      console.log("chunks: ", chunks);

      // Upload parts with delay between batches
      const parts: UploadPart[] = [];
      for (let i = 0; i < chunks.length; i += maxConcurrentUploads) {
        const uploadPromises = chunks
          .slice(i, i + maxConcurrentUploads)
          .map(async (chunk, index) => {
            const partNumber = i + index + 1;
            // Add delay between individual part uploads
            await delay(1000 * index); // 1 second delay between parts in same batch
            const presignedUrl = await getPresignedUrlForPart(
              partNumber,
              file.name,
            );
            return uploadPart(partNumber, chunk, presignedUrl, chunks.length);
          });

        const completedParts = await Promise.all(uploadPromises);
        parts.push(...completedParts);

        // Add delay between batches
        if (i + maxConcurrentUploads < chunks.length) {
          await delay(2000); // 2 seconds delay between batches
        }
      }

      // Complete multipart upload
      // await completeMultipartUpload(parts.sort((a, b) => a.PartNumber - b.PartNumber));

      // Handle successful upload
      handleSuccessFullUpload(file, s3_key, "");
    } catch (error) {
      handleError(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setIsUploading(false);
    }
  }, [file, folderId]);

  // const completeMultipartUpload = async (parts: UploadPart[]) => {
  //   const { authenticatedCall } = actionsHandler("completeMultipartUpload");
  //   const response = await dispatch(
  //     authenticatedCall({
  //       method: "post",
  //       payload: {
  //         upload_id: uploadId,
  //         parts: parts,
  //         s3_key: s3Key,
  //       },
  //     })
  //   );

  //   if (!response.ok) throw new Error("Failed to complete multipart upload");
  //   return response.data;
  // };

  const handleSaveFile = useCallback(
    async (payload: Payload) => {
      const { authenticatedCall: saveFile } = actionsHandler(
        (endpoint as EndpointType) ||
          (isDriveUpload ? "myDriveFiles" : "fileSave"),
      );
      const saveResponse = await dispatch(
        saveFile({
          method: isUpdate ? "put" : "post",
          payload: payload,
          customParams: updateId ? [String(updateId) + "/"] : undefined,
        }),
      );

      if (saveResponse.ok) {
        toast.success(
          saveResponse.data.message || "File uploaded successfully",
        );
        onUploadComplete?.(saveResponse.data);
      } else {
        throw new Error(
          saveResponse.data.message || "Failed to save file metadata",
        );
      }
    },
    [isDriveUpload],
  );

  const handleSuccessFullUpload = useCallback(
    async (file: File, s3_key: string, version_id: string) => {
      if (!file) return;

      const payload: Payload = {
        folder_id: folderId,
        file_name: file.name,
        s3_key: s3_key,
        version_id: version_id,
        size: file.size,
      };

      if (documentTypeId) {
        payload["document_master_id"] = documentTypeId;
      }
      if (onSuccessUploadCallback) {
        onSuccessUploadCallback(payload);
      } else {
        handleSaveFile(payload);
      }
    },
    [handleSaveFile, onUploadComplete],
  );

  useEffect(() => {
    if (file) {
      handleUploadingFile();
    }
  }, [handleUploadingFile]);

  useEffect(() => {
    if (cancelUpload) {
      currentRequest?.abort();
      setCancelUpload(false);
      setUploadProgress(0);
      setIsUploading(false);
      setFile(null);
      onUploadError?.("Upload cancelled");
      toast.warning("Upload cancelled");
    }
  }, [cancelUpload]);

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setUploadProgress(0);
    setFile(null);
    onUploadError?.(errorMessage);
    toast.error(errorMessage);
  };

  const handleSavingMarkAsNA = useCallback(
    async (type: "user" | "default" = "default", isNA: boolean) => {
      setFile(null);
      setError("");
      setUploadProgress(0); // Reset upload progress
      if (type === "user" && isNA) {
        const { authenticatedCall: saveFile } = actionsHandler(
          isDriveUpload ? "myDriveFiles" : "fileSave",
        );

        const saveResponse = await dispatch(
          saveFile({
            method: "post",
            payload: {
              folder_id: folderId,
              file_name: null,
              s3_key: null,
              version_id: null,
              document_master_id: documentTypeId,
              size: null,
              mark_as_na: isNA,
            },
          }),
        );
        if (saveResponse.ok) {
          toast.success(saveResponse.data.message);
          setForceRefresh?.(true);
        }
      }
      onNAChange?.(isNA);
    },
    [
      isDriveUpload,
      documentTypeId,
      folderId,
      dispatch,
      onNAChange,
      setForceRefresh,
    ],
  );

  return (
    <FileUpload
      label={label}
      file={file}
      onFileChange={handleFileChange}
      error={error}
      accept={accept}
      maxFileSize={maxFileSize}
      id={id}
      uploadProgress={uploadProgress}
      setUploadProgress={setUploadProgress}
      isNA={isNA}
      disableIsNA={disableIsNA}
      onNAChange={handleSavingMarkAsNA}
      selectedDocument={selectedDocument}
      setCancelUpload={setCancelUpload}
      isUploading={isUploading}
      setIsUploading={setIsUploading}
    />
  );
};

export default S3FileUpload;
